/**
 * File: apps/qbench-cp/reactapp/src/shared/hooks/misc/useSourceParameters.js
 *
 * Purpose: Determines whether the Sources feature is enabled/visible and whether a new source
 *     can be created from the order request flow.
 *
 * Scope: Sources feature gating (module visibility, order-request create button).
 *
 * Tags: customer-portal, sources
 *
 * Overview: Combines a SOURCES module-enabled permission check, RBAC access checks for the
 *     sources route and the order-request "create source" button, and the Sample entity's
 *     Source field configuration (whether it's shown globally and whether it's shown on the
 *     order request form) to compute isEnabled and canCreate flags.
 *
 * Exports: default useSourceParameters hook.
 */

import { useFieldConfig } from 'context/fields';
import isEmpty from 'lodash/isEmpty';
import policies from 'shared/configs/rbac/accessPolicies';
import { EntityType } from 'shared/constants/entityTypes';
import { ENABLED_MODULES } from 'shared/constants/fields';
import useAccessControl from './useAccessControl';
import usePermissionCheck from './usePermissionCheck';

export default function useSourceParameters() {
  const { getFields } = useFieldConfig();
  const sampleFieldConfigs = getFields({ entityType: EntityType.SAMPLE });
  const moduleEnabled = usePermissionCheck(ENABLED_MODULES.SOURCES);
  const hasReadPermission = useAccessControl(policies.route.sources);
  const hasCreatePermission = useAccessControl(
    policies.ui.sourceDropdownCreateBtn,
  );

  const sourceFieldConfig = (sampleFieldConfigs || []).find(
    (f) => f.name.toUpperCase() === EntityType.SOURCE,
  );
  const fieldShown = {
    globally: !isEmpty(sourceFieldConfig),
    onOrderRequest:
      !isEmpty(sourceFieldConfig) && sourceFieldConfig.orderRequestField,
  };
  const isEnabled = moduleEnabled && fieldShown.globally && hasReadPermission;
  const canCreate =
    moduleEnabled && hasCreatePermission && fieldShown.onOrderRequest;

  return { canCreate, isEnabled };
}
